import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { analytics } from "@heycatch/sdk";
import "@/index.css";
import "@/i18n";
import App from "@/App";

analytics.init({
  projectKey: "hck_pk_8qwOSbPLAc42DWcleZGFsjVRs10JpWAC",
  install: {
    framework: "react",
    frameworkVersion: "19",
    agent: "other",
  },
});

// Capture Chrome/Android's install prompt as early as possible — before any
// React component mounts — so the "Install" button is a true one-tap install
// no matter which page the visitor lands on first.
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  window.__eviqInstallPrompt = e;
  window.dispatchEvent(new Event("eviq:install-ready"));
});
window.addEventListener("appinstalled", () => {
  window.__eviqInstallPrompt = null;
  try { localStorage.setItem("eviq_pwa_installed", "1"); } catch (e) {}
  window.dispatchEvent(new Event("eviq:app-installed"));
});
if (process.env.NODE_ENV === "production" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// PWA auto-update: installed apps resume suspended sessions without ever
// refetching /app, so users can be pinned to a stale bundle for days. On
// launch and whenever the app returns to the foreground, compare the loaded
// bundle to the server's asset-manifest and reload once if a newer build
// shipped. Throttled + loop-guarded.
if (process.env.NODE_ENV === "production") {
  let lastUpdateCheck = 0;
  const checkForUpdate = async () => {
    if (Date.now() - lastUpdateCheck < 60_000) return;
    lastUpdateCheck = Date.now();
    try {
      const res = await fetch("/asset-manifest.json", { cache: "no-store" });
      if (!res.ok) return;
      const latest = (await res.json())?.files?.["main.js"];
      if (!latest) return;
      const current = Array.from(document.scripts)
        .map((s) => s.src)
        .find((s) => /\/static\/js\/main\./.test(s));
      if (!current || current.includes(latest)) return;
      // Reload at most once per new bundle to avoid loops on CDN lag.
      const guard = sessionStorage.getItem("eviq_update_reload");
      if (guard === latest) return;
      sessionStorage.setItem("eviq_update_reload", latest);
      window.location.reload();
    } catch (e) {}
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkForUpdate();
  });
  window.addEventListener("focus", checkForUpdate);
  window.addEventListener("load", checkForUpdate);
  // Apps left open across a deploy still pick it up within 15 minutes.
  setInterval(checkForUpdate, 15 * 60_000);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
